/**
 * 30 Day Review (XLOGS-172) - constants (text, ids, options).
 *
 * Colors/spacing come from the MUI theme via `useTheme()` in the components (not hardcoded, and not
 * via the deprecated `usePalette()`/`getSizing()` helpers). The layout follows the
 * "14.) 30 Day Review Wireframe"; visual styling uses the X Logs design system.
 */

import { MeetingType } from "./types";

export const TDR_TITLE = "30 Day RA Supervision Review";

export const MEETING_TYPES: MeetingType[] = [
  "Face to Face",
  "Phone Call",
  "Observation",
  "Virtual Synchronous",
];

/** Exact attestation language required by the ticket (Section 3). Do not alter wording. */
export const ATTESTATION_TEXT =
  "Unless noted, school was in session and the student was in attendance on all days recorded; " +
  "form edited to correctly reflect services delivered; sessions affirmed as medically necessary " +
  "and in accordance with the care plan.";

export const ATTENTION_MODAL_BODY =
  "In order to finalize your documentation, the below items were identified as being required by " +
  "your administrator or necessary to evaluate opportunity for Medicaid billing. Please click the " +
  "findings below to update accordingly.";

/** Electronic signature copy. Mirrors ElectronicSignatureConstants.DOCUMENT_TEXT in the app. */
export const ESIGNATURE_BODY =
  "I hereby authorize that the information notated in this session is correct, accurate and complete";

/** DOM ids used by the Attention modal to scroll-to and focus incomplete fields. */
export const FIELD_IDS = {
  raName: "tdr-ra-name",
  reviewHeld: "tdr-review-held",
  reviewNotHeldReason: "tdr-review-not-held-reason",
  meetingType: "tdr-meeting-type",
  nextReviewDate: "tdr-next-review-date",
  areasCovered: "tdr-areas-covered",
  raTraining: "tdr-ra-training",
  trainingList: "tdr-training-list",
  raEffectiveness: "tdr-ra-effectiveness",
  attestation: "tdr-attestation",
} as const;

/**
 * Id of the visible label element for a text field, so the input can point at it with
 * `aria-labelledby` (giving screen readers the real label, not the placeholder). Text inputs keep
 * their FIELD_IDS id as the Attention-modal scroll target; radio/chip groups instead put FIELD_IDS
 * directly on their label and reference that, so this helper is only for the single-input fields.
 */
export const labelIdFor = (fieldId: string) => `${fieldId}-label`;

/**
 * Matches a "30 Day Review" service by name. The service has no stable id today (flagged to backend
 * in PRODUCT_QUESTIONS / ticket open questions), so name-matching mirrors the existing check in
 * `views/modals/edit_session_form_wrapper.tsx`.
 */
export const is30DayReviewServiceName = (name: string | undefined | null): boolean =>
  /30\s*day\s*review/i.test(name ?? "");

/**
 * Configuration key gating the entire 30 Day Review feature (route, scheduler service option,
 * calendar events). Must match the boolean key in the App Configuration store; when the key is absent
 * the selector returns undefined (falsy), so the feature is OFF by default until the key is created and
 * enabled.
 */
export const THIRTY_DAY_REVIEW_FLAG = "ThirtyDayReviewActive";
